import { Component, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BackendAccessService } from '../../../../Services/APICalls/backend-access-service';
import { LoadingService } from '../../../../Services/Loading/loading-service.service';
import { POSubscriptionDetails, UpdatePOSubscriptionRequest } from '../../../../interfaces/po-subscription.interface';

@Component({
  selector: 'app-po-details-page',
  imports: [
    MatIconModule,
    MatButtonModule,
    FormsModule,
    CommonModule,
  ],
  templateUrl: './po-details-page.html',
  styleUrl: './po-details-page.scss',
})
export class PoDetailsPage implements OnInit {
  isPropertyEditMode = false;
  isIntegratorEditMode = false;
  subscriptionId: string | null = null;
  subscriptionData: POSubscriptionDetails | null = null;
  originalSubscriptionData: POSubscriptionDetails | null = null;
  dataWasUpdated = false;
  updateErrorMessage: string = '';
  updateSuccessMessage: string = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly backend: BackendAccessService,
    private readonly loadingService: LoadingService,
    private readonly router: Router
  ) {}

  /* istanbul ignore next */
  isValidEmail(email: string | undefined): boolean {
    if (!email) return false;
    const trimmed = email.trim();
    return trimmed.includes('@') && trimmed.includes('.');
  }

  private static readonly PROPERTY_EDIT_FIELDS = new Set([
    'propertyName',
    'propertyAddress',
    'propertyMainPoc',
    'propertyMainPocEmail',
    'unitDeviceQuantity',
    'commonAreaDeviceQuantity',
  ]);
  private static readonly INTEGRATOR_EDIT_FIELDS = new Set([
    'integratorCompanyName',
    'integratorMainPoc',
    'integratorMainPocEmail',
  ]);
  private static readonly DEVICE_QUANTITY_FIELDS = new Set([
    'unitDeviceQuantity',
    'commonAreaDeviceQuantity',
  ]);

  /* istanbul ignore next */
  private isEditModeFieldSkipped(fieldName: string): boolean {
    if (PoDetailsPage.PROPERTY_EDIT_FIELDS.has(fieldName)) {
      return !this.isPropertyEditMode;
    }
    if (PoDetailsPage.INTEGRATOR_EDIT_FIELDS.has(fieldName)) {
      return !this.isIntegratorEditMode;
    }
    return false;
  }

  /* istanbul ignore next */
  isFieldInvalid(fieldName: string): boolean {
    if (!this.subscriptionData) return false;

    // Only show validation errors if in edit mode
    if (this.isEditModeFieldSkipped(fieldName)) return false;

    if (PoDetailsPage.DEVICE_QUANTITY_FIELDS.has(fieldName)) {
      const quantity = (this.subscriptionData as any)[fieldName];
      return quantity == null || quantity < 0;
    }

    if (fieldName === 'propertyMainPocEmail') {
      return !this.isValidEmail(this.subscriptionData.propertyMainPocEmail);
    }

    if (fieldName === 'integratorMainPocEmail') {
      return !this.isValidEmail(this.subscriptionData.integratorMainPocEmail);
    }

    const value = (this.subscriptionData as any)[fieldName];

    if (typeof value === 'string') {
      return !value?.trim();
    }

    return value == null;
  }

  /* istanbul ignore next */
  isFormValid(): boolean {
    if (!this.subscriptionData) {
      return false;
    }

    // Validate property fields if in edit mode
    if (this.isPropertyEditMode) {
      if (
        !this.subscriptionData.propertyName?.trim() ||
        !this.subscriptionData.propertyAddress?.trim() ||
        !this.subscriptionData.propertyMainPoc?.trim() ||
        !this.isValidEmail(this.subscriptionData.propertyMainPocEmail) ||
        this.subscriptionData.unitDeviceQuantity == null ||
        this.subscriptionData.unitDeviceQuantity < 0 ||
        this.subscriptionData.commonAreaDeviceQuantity == null ||
        this.subscriptionData.commonAreaDeviceQuantity < 0
      ) {
        return false;
      }
    }

    // Validate integrator fields if in edit mode
    if (this.isIntegratorEditMode) {
      if (
        !this.subscriptionData.integratorCompanyName?.trim() ||
        !this.subscriptionData.integratorMainPoc?.trim() ||
        !this.isValidEmail(this.subscriptionData.integratorMainPocEmail)
      ) {
        return false;
      }
    }

    return true;
  }

  /* istanbul ignore next */
  hasChanges(): boolean {
    if (!this.subscriptionData || !this.originalSubscriptionData) {
      return false;
    }

    // Check property fields
    if (this.isPropertyEditMode) {
      if (
        this.subscriptionData.propertyName !== this.originalSubscriptionData.propertyName ||
        this.subscriptionData.propertyAddress !== this.originalSubscriptionData.propertyAddress ||
        this.subscriptionData.propertyMainPoc !== this.originalSubscriptionData.propertyMainPoc ||
        this.subscriptionData.propertyMainPocEmail !== this.originalSubscriptionData.propertyMainPocEmail ||
        this.subscriptionData.unitDeviceQuantity !== this.originalSubscriptionData.unitDeviceQuantity ||
        this.subscriptionData.commonAreaDeviceQuantity !== this.originalSubscriptionData.commonAreaDeviceQuantity ||
        this.subscriptionData.isEngageMigration !== this.originalSubscriptionData.isEngageMigration ||
        this.subscriptionData.siteId !== this.originalSubscriptionData.siteId
      ) {
        return true;
      }
    }

    // Check integrator fields
    if (this.isIntegratorEditMode) {
      if (
        this.subscriptionData.integratorCompanyName !== this.originalSubscriptionData.integratorCompanyName ||
        this.subscriptionData.integratorMainPoc !== this.originalSubscriptionData.integratorMainPoc ||
        this.subscriptionData.integratorMainPocEmail !== this.originalSubscriptionData.integratorMainPocEmail
      ) {
        return true;
      }
    }

    return false;
  }

  /* istanbul ignore next */
  canUpdate(): boolean {
    return this.isFormValid() && this.hasChanges();
  }
  /* istanbul ignore next */
  ngOnInit() {
    this.subscriptionId = this.route.snapshot.paramMap.get('id');
    if (this.subscriptionId) {
      this.loadSubscriptionData(this.subscriptionId);
    }
  }
  /* istanbul ignore next */
  loadSubscriptionData(recordId: string) {
    this.loadingService.show();

    this.backend.getPOSubById(recordId).subscribe({
      next: (response) => {
        console.log('PO Subscription Data:', response);
        this.subscriptionData = response.currentSubscriptionDetails;
        // Manually set recordId since API doesn't return it in the details object
        if (this.subscriptionData) {
          this.subscriptionData.recordId = recordId;
        }
        this.originalSubscriptionData = JSON.parse(JSON.stringify(this.subscriptionData));
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading PO subscription:', error);
        this.loadingService.hide();
      },
    });
  }
  /* istanbul ignore next */
  goBack() {
    // Navigate back to landing page with PO Subscriptions tab selected (tab index 1)
    // Add refresh param if data was updated successfully
    this.router.navigate(['/zentra-subscriptions'], {
      queryParams: { tab: 1, refresh: this.dataWasUpdated ? 'true' : undefined },
    });
  }
  /* istanbul ignore next */
  togglePropertyEdit() {
    if (this.isPropertyEditMode) {
      // Canceling - restore original values
      if (this.originalSubscriptionData) {
        this.subscriptionData!.propertyName = this.originalSubscriptionData.propertyName;
        this.subscriptionData!.propertyAddress = this.originalSubscriptionData.propertyAddress;
        this.subscriptionData!.propertyMainPoc = this.originalSubscriptionData.propertyMainPoc;
        this.subscriptionData!.propertyMainPocEmail = this.originalSubscriptionData.propertyMainPocEmail;
        this.subscriptionData!.unitDeviceQuantity = this.originalSubscriptionData.unitDeviceQuantity;
        this.subscriptionData!.commonAreaDeviceQuantity = this.originalSubscriptionData.commonAreaDeviceQuantity;
        this.subscriptionData!.isEngageMigration = this.originalSubscriptionData.isEngageMigration;
        this.subscriptionData!.siteId = this.originalSubscriptionData.siteId;
      }
    }
    this.isPropertyEditMode = !this.isPropertyEditMode;
    // Clear messages when toggling edit mode
    this.updateErrorMessage = '';
    this.updateSuccessMessage = '';
  }
  /* istanbul ignore next */
  toggleIntegratorEdit() {
    if (this.isIntegratorEditMode) {
      // Canceling - restore original values
      if (this.originalSubscriptionData) {
        this.subscriptionData!.integratorCompanyName = this.originalSubscriptionData.integratorCompanyName;
        this.subscriptionData!.integratorMainPoc = this.originalSubscriptionData.integratorMainPoc;
        this.subscriptionData!.integratorMainPocEmail = this.originalSubscriptionData.integratorMainPocEmail;
      }
    }
    this.isIntegratorEditMode = !this.isIntegratorEditMode;
    // Clear messages when toggling edit mode
    this.updateErrorMessage = '';
    this.updateSuccessMessage = '';
  }
  /* istanbul ignore next */
  updateSubscription() {
    if (!this.subscriptionData) {
      return;
    }

    // Clear previous messages
    this.updateErrorMessage = '';
    this.updateSuccessMessage = '';

    console.log('subscriptionData.recordId:', this.subscriptionData.recordId);
    console.log('Full subscriptionData:', this.subscriptionData);

    const payload: UpdatePOSubscriptionRequest = {
      recordId: this.subscriptionData.recordId,
      subscriptionId: this.subscriptionData.subscriptionId,
      propertyName: this.subscriptionData.propertyName,
      propertyAddress: this.subscriptionData.propertyAddress,
      propertyMainPoc: this.subscriptionData.propertyMainPoc,
      propertyMainPocEmail: this.subscriptionData.propertyMainPocEmail,
      unitDeviceQuantity: this.subscriptionData.unitDeviceQuantity,
      commonAreaDeviceQuantity: this.subscriptionData.commonAreaDeviceQuantity,
      isEngageMigration: this.subscriptionData.isEngageMigration,
      siteId: this.subscriptionData.siteId,
      integratorCompanyName: this.subscriptionData.integratorCompanyName,
      integratorMainPoc: this.subscriptionData.integratorMainPoc,
      integratorMainPocEmail: this.subscriptionData.integratorMainPocEmail,
      renewalDateNotes: this.subscriptionData.renewalDateNotes,
    };

    console.log('Update payload:', payload);

    this.loadingService.show();

    this.backend.updatePOSubscription(payload).subscribe({
      next: (response) => {
        console.log('Update response:', response);
        if (response.isSuccess) {
          this.updateSuccessMessage = 'Subscription updated successfully! Changes have been saved.';
          // Update the original data to reflect saved changes
          this.originalSubscriptionData = JSON.parse(JSON.stringify(this.subscriptionData));
          // Exit edit modes
          this.isPropertyEditMode = false;
          this.isIntegratorEditMode = false;
          // Mark that data was updated so we refresh on navigation back
          this.dataWasUpdated = true;
        } else {
          this.updateErrorMessage = response.errors || 'Update failed. Please check your changes and try again.';
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error updating subscription:', error);
        this.updateErrorMessage = 'An error occurred while updating the subscription. Please try again.';
        this.loadingService.hide();
      },
    });
  }
}
